// src/views/pages/restaurant-reservations/waitlist/components/DetailsDrawer.jsx
import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Drawer, Space, Card, Typography, Tag, Alert, Divider, Button, Popconfirm, Select } from "antd";
import { MapPin, RotateCcw, CheckCircle, XCircle, Bell, Phone, Table as TableIcon } from "react-feather";
import { tableStatusPill, pill } from "../utils";
import { motion } from "framer-motion";

const { Text } = Typography;

function DetailsDrawer({
  drawerOpen,
  setDrawerOpen,
  selected,
  selectedTable,
  alternatives,
  reassignTable,
  seatGuest,
  notifyGuest,
  setReassignOpen,
  offlineMode,
  safeNotify,
  updateWaitlistEntry,
  logAudit,
  // ✅ add tables if you can (recommended). If you can’t, it still works using alternatives only.
  tables = [],
}) {
  const partySize = Number(selected?.partySize || 2);

  // ✅ Candidate tables: always show options
  const availableTables = useMemo(() => {
    const list = Array.isArray(tables) ? tables : [];

    // Only available + capacity match
    const candidates = list
      .filter((t) => String(t.status || "").toLowerCase() === "available")
      .filter((t) => Number(t.capacity || 0) >= partySize);

    // Prefer same location if selected has preference
    const preferredLoc = selected?.location || selectedTable?.location || null;
    if (!preferredLoc) return candidates;

    const sameLoc = candidates.filter((t) => String(t.location || "") === String(preferredLoc));
    const other = candidates.filter((t) => String(t.location || "") !== String(preferredLoc));
    return [...sameLoc, ...other];
  }, [tables, partySize, selected?.location, selectedTable?.location]);

  // Merge alternatives + availableTables (unique by id)
  const suggestedTables = useMemo(() => {
    const map = new Map();
    (Array.isArray(alternatives) ? alternatives : []).forEach((t) => map.set(String(t.id), t));
    availableTables.forEach((t) => map.set(String(t.id), t));
    return Array.from(map.values()).slice(0, 10);
  }, [alternatives, availableTables]);

  // ✅ table selection state
  const [seatTableId, setSeatTableId] = useState(null);

  // Default selection: current assigned table → first suggestion
  useEffect(() => {
    if (!drawerOpen) return;
    const defaultId = selectedTable?.id || suggestedTables[0]?.id || null;
    setSeatTableId(defaultId ? String(defaultId) : null);
  }, [drawerOpen, selectedTable?.id, suggestedTables]);

  const seatTableObj = useMemo(() => {
    if (!seatTableId) return null;
    return (
      suggestedTables.find((t) => String(t.id) === String(seatTableId)) ||
      availableTables.find((t) => String(t.id) === String(seatTableId)) ||
      null
    );
  }, [seatTableId, suggestedTables, availableTables]);

  const canNotify = !!selected?.phone && selected?.status !== "canceled" && selected?.status !== "seated";
  const canSeat =
    selected?.status !== "canceled" &&
    selected?.status !== "seated" &&
    !!seatTableId &&
    (seatTableObj ? String(seatTableObj.status || "").toLowerCase() === "available" : true);

  const seatGuidance = useMemo(() => {
    if (selected?.status === "canceled") return { type: "warning", msg: "This entry is canceled." };
    if (selected?.status === "seated") return { type: "success", msg: "This guest is already seated." };
    if (!seatTableId) return { type: "warning", msg: "Select a table to seat this guest." };
    if (seatTableObj && String(seatTableObj.status || "").toLowerCase() !== "available")
      return { type: "warning", msg: "Selected table is not available." };
    return { type: "success", msg: "Select a table and seat the guest when ready." };
  }, [selected?.status, seatTableId, seatTableObj]);

  return (
    <Drawer
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      width={520}
      title={
        <Space>
          <div style={{ fontWeight: 900 }}>{selected?.name || "Guest Details"}</div>
          {selected?.id ? <Tag>{selected.id}</Tag> : null}
          {selected?.isVIP ? <Tag color="gold">VIP</Tag> : null}
        </Space>
      }
      styles={{ body: { padding: 16 } }}
    >
      <motion.div initial={{ x: 50, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.25 }}>
        {!selected ? null : (
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            {/* Guest */}
            <Card style={{ borderRadius: 14, border: "1px solid #eef2f7" }}>
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                <Space style={{ width: "100%", justifyContent: "space-between" }}>
                  <Text strong>Guest Info</Text>
                  {selected?.status ? pill("blue", String(selected.status).toUpperCase()) : null}
                </Space>

                <Space wrap>
                  {pill("blue", `${selected.partySize || "-"} guests`)}
                  {pill("default", selected.location || selectedTable?.location || "-", <MapPin size={12} />)}
                  {selected.phone
                    ? pill("default", selected.phone, <Phone size={12} />)
                    : pill("default", "No Phone", <Phone size={12} />)}
                </Space>

                {selected.notes ? (
                  <Alert
                    type={String(selected.notes).toLowerCase().includes("allergy") ? "error" : "info"}
                    showIcon
                    style={{ borderRadius: 12 }}
                    message={selected.notes}
                  />
                ) : null}
              </Space>
            </Card>

            {/* Table Assignment */}
            <Card style={{ borderRadius: 14, border: "1px solid #eef2f7" }}>
              <Space direction="vertical" size={10} style={{ width: "100%" }}>
                <Space style={{ width: "100%", justifyContent: "space-between" }}>
                  <Text strong>Table Assignment</Text>
                  {selectedTable ? tableStatusPill(selectedTable.status) : pill("default", "Unassigned")}
                </Space>

                <div>
                  <Text type="secondary">Current Table</Text>
                  <div style={{ fontSize: 16, fontWeight: 900 }}>
                    {selectedTable ? `${selectedTable.label} (${selectedTable.capacity} seats)` : "Not Assigned"}
                  </div>
                  <div style={{ color: "#6b7280" }}>
                    {selectedTable ? selectedTable.location : "Choose a table below to seat the guest."}
                  </div>
                </div>

                {/* ✅ FIX: no stray comment rendering */}
                <Alert
                  type={seatGuidance.type}
                  showIcon
                  style={{ borderRadius: 12 }}
                  message={seatGuidance.msg}
                />

                <Divider style={{ margin: "6px 0" }} />

                <Space style={{ width: "100%", justifyContent: "space-between" }}>
                  <Text strong>Choose a Table</Text>
                  <Text type="secondary">
                    {availableTables.length} available
                  </Text>
                </Space>

                {/* Suggested table chips */}
                {suggestedTables.length > 0 ? (
                  <Space wrap>
                    {suggestedTables.slice(0, 8).map((t) => {
                      const selectedChip = String(seatTableId) === String(t.id);
                      return (
                        <Button
                          key={t.id}
                          type={selectedChip ? "primary" : "default"}
                          style={{ borderRadius: 12, height: 38 }}
                          icon={<TableIcon size={14} />}
                          onClick={() => setSeatTableId(String(t.id))}
                        >
                          {t.label} ({t.capacity}) • {t.location}
                        </Button>
                      );
                    })}
                  </Space>
                ) : (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ borderRadius: 12 }}
                    message="No available tables match this party size right now."
                  />
                )}

                {/* Dropdown fallback (useful when many tables) */}
                {availableTables.length > 0 ? (
                  <Select
                    value={seatTableId || undefined}
                    onChange={(v) => setSeatTableId(String(v))}
                    style={{ width: "100%" }}
                    placeholder="Or select from all available tables"
                    options={availableTables.map((t) => ({
                      value: String(t.id),
                      label: `${t.label} (${t.capacity}) • ${t.location}`,
                    }))}
                  />
                ) : null}

                {/* Quick reassign from alternatives (keeps your original behavior too) */}
                {alternatives && alternatives.length > 0 ? (
                  <>
                    <Divider style={{ margin: "6px 0" }} />
                    <Text strong>Alternative Tables</Text>
                    <Space wrap>
                      {alternatives.map((t) => (
                        <Button
                          key={t.id}
                          style={{ borderRadius: 12 }}
                          icon={<RotateCcw size={14} />}
                          onClick={() => reassignTable(selected, t.id)}
                        >
                          Reassign to {t.label} ({t.capacity})
                        </Button>
                      ))}
                    </Space>
                  </>
                ) : null}
              </Space>
            </Card>

            {/* Actions */}
            <Card style={{ borderRadius: 14, border: "1px solid #eef2f7" }}>
              <Space direction="vertical" size={10} style={{ width: "100%" }}>
                <Text strong>Quick Actions</Text>

                <Space wrap>
                  <Button
                    icon={<Bell size={16} />}
                    onClick={() => notifyGuest(selected)}
                    disabled={!canNotify}
                    style={{ borderRadius: 12, height: 40 }}
                  >
                    Notify Guest
                  </Button>

                  <Button
                    icon={<RotateCcw size={16} />}
                    onClick={() => setReassignOpen(true)}
                    style={{ borderRadius: 12, height: 40 }}
                  >
                    Reassign Table
                  </Button>

                  <Button
                    type="primary"
                    icon={<CheckCircle size={16} />}
                    style={{ borderRadius: 12, height: 40 }}
                    disabled={!canSeat}
                    onClick={async () => {
                      if (!seatTableId) return safeNotify("warning", "Select a table first.");
                      await seatGuest(selected, seatTableId);
                      setDrawerOpen(false);
                    }}
                  >
                    Seat Now
                  </Button>

                  <Popconfirm
                    title="Confirm cancel?"
                    onConfirm={async () => {
                      await updateWaitlistEntry(selected.id, { status: "canceled" });
                      safeNotify("success", "Cancelled");
                      logAudit("Receptionist", `Cancelled ${selected.name}`);
                      setDrawerOpen(false);
                    }}
                  >
                    <Button danger icon={<XCircle size={16} />} style={{ borderRadius: 12, height: 40 }}>
                      Cancel
                    </Button>
                  </Popconfirm>
                </Space>

                {offlineMode ? (
                  <Alert type="warning" showIcon style={{ borderRadius: 12 }} message="Offline: Actions queued for sync." />
                ) : null}
              </Space>
            </Card>
          </Space>
        )}
      </motion.div>
    </Drawer>
  );
}

DetailsDrawer.propTypes = {
  drawerOpen: PropTypes.bool.isRequired,
  setDrawerOpen: PropTypes.func.isRequired,
  selected: PropTypes.object,
  selectedTable: PropTypes.object,
  alternatives: PropTypes.array.isRequired,
  reassignTable: PropTypes.func.isRequired,
  seatGuest: PropTypes.func.isRequired,
  notifyGuest: PropTypes.func.isRequired,
  setReassignOpen: PropTypes.func.isRequired,
  offlineMode: PropTypes.bool.isRequired,
  safeNotify: PropTypes.func.isRequired,
  updateWaitlistEntry: PropTypes.func.isRequired,
  logAudit: PropTypes.func.isRequired,
  tables: PropTypes.array, // ✅ NEW (recommended)
};

export default DetailsDrawer;
